import { inject, Injectable } from '@angular/core';
import { AppConfigService } from '@app/app-config.service';

declare global {
  interface Window {
    grecaptcha?: {
      ready: (cb: () => void) => void;
      execute: (siteKey: string, options: { action: string }) => Promise<string>;
    };
  }
}

@Injectable({ providedIn: 'root' })
export class RecaptchaService {
  private cfg = inject(AppConfigService);
  private loadPromise?: Promise<void>;

  private get siteKey(): string {
    return this.cfg.recaptchaSiteKey;
  }

  private loadScript(): Promise<void> {
    if (this.loadPromise) {
      return this.loadPromise;
    }

    if (!this.siteKey) {
      this.loadPromise = Promise.resolve();
      return this.loadPromise;
    }

    if (typeof window === 'undefined') {
      this.loadPromise = Promise.resolve();
      return this.loadPromise;
    }

    if (window.grecaptcha) {
      this.loadPromise = Promise.resolve();
      return this.loadPromise;
    }

    this.loadPromise = new Promise((resolve, reject) => {
      const existing = document.getElementById('recaptcha-v3');
      if (existing) {
        existing.addEventListener('load', () => resolve());
        existing.addEventListener('error', () => reject(new Error('reCaptcha Script konnte nicht geladen werden.')));
        return;
      }

      const script = document.createElement('script');
      script.id = 'recaptcha-v3';
      script.async = true;
      script.defer = true;
      script.src = `https://www.google.com/recaptcha/api.js?render=${this.siteKey}`;
      script.onload = () => resolve();
      script.onerror = () => reject(new Error('reCaptcha Script konnte nicht geladen werden.'));
      document.head.appendChild(script);
    });

    return this.loadPromise;
  }

  async getToken(action: string): Promise<string> {
    if (!this.siteKey) {
      return '';
    }

    try {
      await this.loadScript();
      const grecaptcha = window.grecaptcha;
      if (!grecaptcha) {
        return '';
      }

      return await new Promise((resolve) => {
        grecaptcha.ready(() => {
          grecaptcha.execute(this.siteKey, { action }).then(resolve).catch(() => resolve(''));
        });
      });
    } catch {
      return '';
    }
  }
}
